import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Bookmark,
  BookmarkCheck,
  Check,
  ChevronDown,
  ChevronUp,
  Download,
  Gavel,
  Link2,
  MessageCircle,
  Phone,
  Search,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function JudgmentDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { lang } = useI18n();
  const isAr = lang === "ar";

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const [copied, setCopied] = useState(false);
  const [activeMatch, setActiveMatch] = useState(0);
  const [fav, setFav] = useState(false);
  const bodyRef = useRef(null);

  const FAV_KEY = "aldakheel_fav_principles";
  useEffect(() => {
    try {
      const arr = JSON.parse(localStorage.getItem(FAV_KEY) || "[]");
      setFav(arr.includes(id));
    } catch { /* noop */ }
  }, [id]);

  const toggleFav = () => {
    let arr = [];
    try {
      arr = JSON.parse(localStorage.getItem(FAV_KEY) || "[]");
    } catch { arr = []; }
    if (arr.includes(id)) {
      arr = arr.filter((x) => x !== id);
      setFav(false);
      toast.success(isAr ? "أُزيل من المفضلة" : "Removed from saved");
    } else {
      arr.push(id);
      setFav(true);
      toast.success(isAr ? "أُضيف إلى المفضلة" : "Saved");
    }
    localStorage.setItem(FAV_KEY, JSON.stringify(arr));
  };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/judgments/${id}`)
      .then((r) => {
        if (!alive) return;
        setDoc(r.data);
      })
      .catch(() => alive && setNotFound(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [id]);

  const t = useMemo(
    () => ({
      back: isAr ? "عودة إلى المبادئ" : "Back to principles",
      case: isAr ? "رقم القضية" : "Case",
      court: isAr ? "المحكمة" : "Court",
      issued: isAr ? "تاريخ الصدور" : "Issued",
      search: isAr ? "ابحث داخل هذا المبدأ..." : "Search inside this principle…",
      download: isAr ? "تحميل PDF" : "Download PDF",
      copy: isAr ? "نسخ الرابط" : "Copy link",
      copied: isAr ? "تم النسخ" : "Copied",
      whatsapp: isAr ? "واتساب" : "WhatsApp",
      askSami: isAr ? "اسأل سامي عن هذا المبدأ" : "Ask Sami about this principle",
      save: isAr ? "حفظ" : "Save",
      saved: isAr ? "محفوظ" : "Saved",
      booking: isAr ? "احجز استشارة" : "Book consultation",
      loading: isAr ? "جارِ التحميل..." : "Loading…",
      notFound: isAr ? "المبدأ غير موجود." : "Principle not found.",
      noMatches: isAr ? "لا نتائج" : "No matches",
      matchesOf: (a, b) => (isAr ? `${a} من ${b}` : `${a} of ${b}`),
    }),
    [isAr]
  );

  const shareUrl = typeof window !== "undefined" ? window.location.href.split("?")[0] : "";
  const waUrl = `https://wa.me/?text=${encodeURIComponent(
    `${doc?.case_number || ""}\n${shareUrl}`
  )}`;

  const copyLink = () => {
    const done = () => {
      setCopied(true);
      toast.success(t.copied);
      window.setTimeout(() => setCopied(false), 1600);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(shareUrl).then(done).catch(done);
    } else {
      done();
    }
  };

  // Deep-link into Sami with a seeded question about this principle.
  const askSami = () => {
    if (!doc) return;
    const parts = [
      isAr
        ? `بخصوص مبدأ محكمة التمييز الكويتية: «${doc.case_number || ""}»`
        : `Regarding this Kuwaiti Court of Cassation principle: "${doc.case_number || ""}"`,
    ];
    if (doc.court) parts.push(isAr ? `الدائرة/المحكمة: ${doc.court}.` : `Chamber/Court: ${doc.court}.`);
    if (doc.summary) parts.push((isAr ? "الملخص: " : "Summary: ") + doc.summary);
    parts.push(
      isAr
        ? "اشرح لي هذا المبدأ القانوني بالتفصيل، وما الذي ينطبق منه على القضايا المماثلة، مع ذكر المواد والقوانين ذات الصلة."
        : "Explain this legal principle in detail, what it applies to in similar cases, and cite the relevant articles and laws."
    );
    const q = parts.join("\n");
    navigate(`/consultant?q=${encodeURIComponent(q)}&ask=1`);
  };

  // Split body into paragraphs for readable rendering.
  const paragraphs = useMemo(() => {
    const text = (doc?.text || "").replace(/\r/g, "");
    return text
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter(Boolean);
  }, [doc]);

  // Count total matches across the body.
  const totalMatches = useMemo(() => {
    const q = query.trim();
    if (!q) return 0;
    const re = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    let n = 0;
    paragraphs.forEach((p) => {
      const m = p.match(re);
      if (m) n += m.length;
    });
    return n;
  }, [query, paragraphs]);

  useEffect(() => {
    setActiveMatch(0);
  }, [query]);

  // Scroll to the active match whenever it changes.
  useEffect(() => {
    if (!totalMatches) return;
    const el = document.getElementById(`jmatch-${activeMatch}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeMatch, totalMatches, query]);

  const gotoMatch = (dir) => {
    if (!totalMatches) return;
    setActiveMatch((cur) => (cur + dir + totalMatches) % totalMatches);
  };

  // Highlight query matches, assigning sequential global ids so we can jump.
  const counterRef = { current: 0 };
  const renderHighlighted = (text, keyPrefix) => {
    const q = query.trim();
    if (!q) return text;
    const re = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi");
    return text.split(re).map((part, j) => {
      if (j % 2 === 1) {
        const gi = counterRef.current++;
        return (
          <mark
            key={`${keyPrefix}-m-${j}`}
            id={`jmatch-${gi}`}
            className="px-0.5 rounded-sm"
            style={{
              background: gi === activeMatch ? "#FFEB3B" : "#FFF59D",
              color: "#1a1a1a",
              boxShadow: gi === activeMatch ? "0 0 0 2px #FDD835" : "none",
            }}
          >
            {part}
          </mark>
        );
      }
      return <React.Fragment key={`${keyPrefix}-t-${j}`}>{part}</React.Fragment>;
    });
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-32 text-center text-slate-500">
        {t.loading}
      </div>
    );
  }
  if (notFound || !doc) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-32 text-center">
        <p className="text-slate-500 mb-6">{t.notFound}</p>
        <Link to="/judgments" className="underline" data-testid="judgment-back-link">
          {t.back}
        </Link>
      </div>
    );
  }

  // reset the highlight counter before each render pass of the body
  counterRef.current = 0;

  return (
    <div data-testid="judgment-detail-page" className="max-w-4xl mx-auto px-6 lg:px-10 py-20">
      <Link
        to="/judgments"
        data-testid="judgment-back-link"
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-8"
      >
        <ArrowRight className={`w-4 h-4 ${isAr ? "" : "rotate-180"}`} />
        {t.back}
      </Link>

      {/* HEADER */}
      <div className="mb-8">
        <div className="flex items-start gap-3">
          <Gavel className="w-6 h-6 shrink-0 mt-1" style={{ color: "var(--gold-deep)" }} />
          <div>
            <h1 className="font-display text-3xl lg:text-4xl leading-tight" dir="rtl">
              {doc.case_number}
            </h1>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-sm text-slate-600">
              {doc.court && (
                <span>
                  <span className="eyebrow">{t.court}:</span> {doc.court}
                </span>
              )}
              {doc.date_issued && (
                <span>
                  <span className="eyebrow">{t.issued}:</span> {doc.date_issued}
                </span>
              )}
              {doc.category && (
                <span
                  className="inline-block px-2.5 py-0.5 text-[11px] rounded-full"
                  style={{ background: "rgba(201,169,97,0.15)", color: "var(--gold-deep)" }}
                >
                  {doc.category}
                </span>
              )}
            </div>
          </div>
        </div>
        {doc.summary && (
          <p className="text-slate-700 leading-relaxed mt-5 border-s-2 ps-4" dir="rtl"
             style={{ borderColor: "var(--gold-deep)" }}>
            {doc.summary}
          </p>
        )}
      </div>

      {/* ACTIONS */}
      <div className="flex flex-wrap gap-3 mb-8">
        <button
          type="button"
          onClick={askSami}
          data-testid="judgment-ask-sami"
          className="inline-flex items-center gap-2 h-11 px-5 text-sm rounded-sm transition-colors hover:opacity-90"
          style={{ background: "var(--gold-deep)", color: "#fff" }}
        >
          <Sparkles className="w-4 h-4" />
          {t.askSami}
        </button>
        <a
          href={`${BACKEND}/api/judgments/${doc.id}/branded-pdf`}
          target="_blank"
          rel="noreferrer"
          data-testid="judgment-download-pdf"
          className="inline-flex items-center gap-2 h-11 px-5 text-sm rounded-sm transition-colors"
          style={{ background: "var(--navy)", color: "#fff" }}
        >
          <Download className="w-4 h-4" />
          {t.download}
        </a>
        <button
          type="button"
          onClick={toggleFav}
          data-testid="judgment-favorite"
          className="inline-flex items-center gap-2 h-11 px-5 text-sm rounded-sm border transition-colors hover:bg-[var(--beige)]"
          style={{ borderColor: "var(--line-strong)", color: fav ? "var(--gold-deep)" : "var(--ink)" }}
        >
          {fav ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
          {fav ? t.saved : t.save}
        </button>
        <button
          type="button"
          onClick={copyLink}
          data-testid="judgment-copy-link"
          className="inline-flex items-center gap-2 h-11 px-5 text-sm rounded-sm border transition-colors hover:bg-[var(--beige)]"
          style={{ borderColor: "var(--line-strong)", color: "var(--ink)" }}
        >
          {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Link2 className="w-4 h-4" />}
          {copied ? t.copied : t.copy}
        </button>
        <a
          href={waUrl}
          target="_blank"
          rel="noreferrer"
          data-testid="judgment-whatsapp"
          className="inline-flex items-center gap-2 h-11 px-5 text-sm rounded-sm border transition-colors hover:bg-[var(--beige)]"
          style={{ borderColor: "var(--line-strong)", color: "#25D366" }}
        >
          <MessageCircle className="w-4 h-4" />
          {t.whatsapp}
        </a>
        <a
          href={`https://wa.me/96569656963?text=${encodeURIComponent(
            (isAr ? "أرغب بحجز استشارة بخصوص: " : "I'd like to book a consultation about: ") + (doc.case_number || "")
          )}`}
          target="_blank"
          rel="noreferrer"
          data-testid="judgment-booking"
          className="inline-flex items-center gap-2 h-11 px-5 text-sm rounded-sm transition-colors"
          style={{ background: "var(--gold-deep)", color: "#fff" }}
        >
          <Phone className="w-4 h-4" />
          {t.booking}
        </a>
      </div>

      {/* IN-TEXT SEARCH */}
      <div className="mb-8 sticky top-20 z-10">
        <div className="flex items-center gap-2 bg-white/90 backdrop-blur border border-slate-200 rounded-sm p-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              dir="auto"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.search}
              data-testid="judgment-search-internal"
              className="rounded-sm h-10 ps-9 border-0 shadow-none focus-visible:ring-0"
            />
          </div>
          {query.trim() && (
            <div className="flex items-center gap-1 shrink-0 pe-1" data-testid="judgment-match-nav">
              <span className="text-xs text-slate-500 min-w-[70px] text-center">
                {totalMatches ? t.matchesOf(activeMatch + 1, totalMatches) : t.noMatches}
              </span>
              <button
                type="button"
                onClick={() => gotoMatch(-1)}
                disabled={!totalMatches}
                data-testid="judgment-match-prev"
                className="p-1.5 rounded-md hover:bg-[var(--beige)] disabled:opacity-40"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => gotoMatch(1)}
                disabled={!totalMatches}
                data-testid="judgment-match-next"
                className="p-1.5 rounded-md hover:bg-[var(--beige)] disabled:opacity-40"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* BODY */}
      <article
        ref={bodyRef}
        dir="rtl"
        data-testid="judgment-body"
        className="prose prose-slate max-w-none leading-[2] text-[15px] text-slate-800"
      >
        {paragraphs.map((p, i) => (
          <p key={i} className="mb-4 whitespace-pre-wrap scroll-mt-32">
            {renderHighlighted(p, `p-${i}`)}
          </p>
        ))}
      </article>
    </div>
  );
}
